import {
  CreateCategoryResponse,
  useDeleteCategory,
  useGetPaginateCategory,
} from '../../api-http/categories';
import { DialogModal } from '../../components';
import { managementTableSx } from '../../components/ManagementPageLayout';
import { AlertBar } from '../../components/AlertBar';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { notifyMsg } from '../../configs';
import { useAuth } from '../../context/auth-context';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { UserRole } from '../../types/user.types';
import { displayDate } from '../../util/dateTime';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useSnack } from '../../util/useSnack';
import { useTable } from '../../util/useTable';
import { RenameForm } from './CategoryRenameForm';
import {
  IconButton,
  LinearProgress,
  TablePagination,
  CircularProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useConfirm } from 'material-ui-confirm';
import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import { Edit3, Trash2 } from 'react-feather';

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

interface CategoryTableProp {
  onRefresh: boolean;
}

export const CategoryTable: FC<CategoryTableProp> = (onRefresh) => {
  const confirm = useConfirm();
  const { switchedOrgId } = useAuth();
  const { showErrSnack, showSuccessSnack } = useSnack();
  const { sortOnClick, pagingParams, tableProps } = useTable();
  const { sortby, order } = pagingParams;

  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<
    CreateCategoryResponse | undefined
  >();
  const isViewOnly = useIsUserRole([UserRole.user, UserRole.layoutEditor]);

  const {
    data: list,
    isLoading,
    error,
    refetch,
  } = useGetPaginateCategory(pagingParams);

  const { error: deleteError, mutate } = useDeleteCategory({
    onSuccess: () => {
      refetch();
      showSuccessSnack(notifyMsg.CATEGORY_DELETE_SUCCESS);
    },
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  const handleEdit = (category: CreateCategoryResponse) => {
    setOpen(true);
    setActiveCategory(category);
  };

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        description: 'Are you sure you want to delete?',
        confirmationText: 'Yes',
      });
      mutate({ id });
    } catch (e) {}
  };

  useEffect(() => {
    refetch();
  }, [open, switchedOrgId, onRefresh]);

  return (
    <>
      {isLoading && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg || deleteError?.msg} />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.name}
                  direction={sortby === SortEnum.name ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.name)}
                >
                  Category title
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Created
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!list?.data?.length && (
              <TableRow>
                {' '}
                <TableCell colSpan={4} align="center">
                  {isLoading ? <CircularProgress /> : 'No Records Found '}
                </TableCell>{' '}
              </TableRow>
            )}
            {list?.data.map((category) => (
              <TableRow
                key={category.name}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="category">
                  {category.name}
                </TableCell>
                <TableCell align="left">
                  {displayDate(category.updatedAt)}
                </TableCell>
                <TableCell align="left">
                  {displayDate(category.createdAt)}
                </TableCell>

                <TableCell align="center">
                  <Stack direction="row" spacing={0.75} justifyContent="center">
                    <Tooltip title="Rename category">
                      <span>
                        <IconButton
                          onClick={() => handleEdit(category)}
                          disabled={isViewOnly}
                          sx={{ ...actionButtonSx, color: '#2563eb' }}
                        >
                          <Edit3 size={15} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete category">
                      <span>
                        <IconButton
                          onClick={() => handleDelete(category._id)}
                          disabled={isViewOnly}
                          sx={{
                            ...actionButtonSx,
                            color: '#dc2626',
                            bgcolor: '#fffafa',
                            borderColor: '#fecaca',
                          }}
                        >
                          <Trash2 size={15} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>
      <DialogModal
        title="Rename category"
        onClose={() => setOpen(false)}
        open={open}
      >
        <RenameForm
          onFinished={() => setOpen(false)}
          category={activeCategory || undefined}
        />
      </DialogModal>
    </>
  );
};

export default CategoryTable;
